import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import Text from 'core-system/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import { AppState } from 'redux/config/store'
import { automatedReportingActions } from 'redux/automatedReporting/automatedReportingSlice'
import { ScheduledReport } from 'redux/automatedReporting/automatedReportingTypes'
import ScheduledReportCreateModal from './ScheduledReportCreateModal'
import ScheduledReportRunsTable from './ScheduledReportRunsTable'
import ScheduledReportsTable from './ScheduledReportsTable'
import { getSegmentNamesById } from '../utils'

const Container = styled.div`
  margin-top: 2.5rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  min-height: 2.5rem;
`

const HistoryContainer = styled.div`
  margin-top: 2rem;
`

const ScheduledReports = React.memo(() => {
  const dispatch = useDispatch()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedScheduledReport, setSelectedScheduledReport] =
    useState<ScheduledReport | null>(null)
  const {
    scheduledReports,
    scheduledReportsLoading,
    scheduledReportsError,
    scheduledReportRuns,
    scheduledReportRunsLoading,
    scheduledReportRunsError,
  } = useSelector((state: AppState) => state.automatedReporting)
  const { allSegments } = useSelector((state: AppState) => state.employer)

  const segmentNamesById = useMemo(
    () => getSegmentNamesById(allSegments),
    [allSegments]
  )

  useEffect(() => {
    dispatch(automatedReportingActions.getScheduledReports())
    dispatch(automatedReportingActions.getScheduledReportRuns())
  }, [dispatch])

  const openCreateModal = () => {
    setSelectedScheduledReport(null)
    setIsCreateModalOpen(true)
  }

  const openEditModal = (scheduledReport: ScheduledReport) => {
    setSelectedScheduledReport(scheduledReport)
    setIsCreateModalOpen(true)
  }

  const closeModal = () => {
    setIsCreateModalOpen(false)
    setSelectedScheduledReport(null)
  }

  return (
    <Container>
      <Header>
        <Text variant='h4'>Scheduled Reports</Text>
        <PrimaryButton onClick={openCreateModal}>
          Create Scheduled Report
        </PrimaryButton>
      </Header>
      <ScheduledReportsTable
        scheduledReports={scheduledReports}
        segmentNamesById={segmentNamesById}
        isLoading={scheduledReportsLoading}
        error={scheduledReportsError}
        onEdit={openEditModal}
      />
      <HistoryContainer>
        <Header>
          <Text variant='h4'>Report History</Text>
        </Header>
        <ScheduledReportRunsTable
          scheduledReportRuns={scheduledReportRuns}
          isLoading={scheduledReportRunsLoading}
          error={scheduledReportRunsError}
        />
      </HistoryContainer>
      <ScheduledReportCreateModal
        open={isCreateModalOpen}
        onClose={closeModal}
        scheduledReport={selectedScheduledReport}
      />
    </Container>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ScheduledReports.displayName = 'ScheduledReports'
}

export default ScheduledReports
